import { Controller } from "stimulus";

export default class extends Controller {
  static values = {
    pollUrl: String,
    readOnly: Boolean,
    pollIntervalMs: { type: Number, default: 3000 },
  };

  static targets = ["aiBanner", "aiBannerText", "applyButton"];

  connect() {
    if (this.readOnlyValue || !this.hasPollUrlValue) return;

    this.userEdited = false;
    this.lastAppliedAt = null;
    this.pendingSuggestion = null;

    this.onFieldChange = () => {
      this.userEdited = true;
    };

    this.fieldInputs().forEach((input) => {
      input.addEventListener("change", this.onFieldChange);
    });

    this.poll = this.poll.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.timer = window.setInterval(this.poll, this.pollIntervalMsValue);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.poll();
  }

  disconnect() {
    if (this.timer) window.clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.fieldInputs().forEach((input) => {
      input.removeEventListener("change", this.onFieldChange);
    });
  }

  onVisibility() {
    if (document.visibilityState === "visible") this.poll();
  }

  async poll() {
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return;

      const data = await response.json();
      if (!data.ai_received_at) return;
      if (data.ai_received_at === this.lastAppliedAt) return;

      const differs = this.suggestionDiffersFromForm(data);
      const formEmpty = !this.selectedValue("image_gradability");

      if (!differs) {
        this.lastAppliedAt = data.ai_received_at;
        return;
      }

      if (formEmpty || (!this.userEdited && !data.has_submission)) {
        this.applySuggestion(data);
        this.lastAppliedAt = data.ai_received_at;
        this.pendingSuggestion = null;
        return;
      }

      this.pendingSuggestion = data;
      this.showPendingBanner(data);
    } catch (_error) {
      // Ignore transient network errors while polling.
    }
  }

  applyPending() {
    if (!this.pendingSuggestion) return;

    this.applySuggestion(this.pendingSuggestion);
    this.lastAppliedAt = this.pendingSuggestion.ai_received_at;
    this.pendingSuggestion = null;
    this.userEdited = false;
    if (this.hasApplyButtonTarget) this.applyButtonTarget.classList.add("hidden");
  }

  applySuggestion(data) {
    this.setRadioValue("image_gradability", data.image_gradability);
    if (data.image_gradability === "gradable" && data.drss_score != null) {
      this.setRadioValue("drss_score", String(data.drss_score));
    } else {
      this.clearRadioGroup("drss_score");
    }

    this.syncDrssVisibility();
    this.showAppliedBanner(data);
  }

  suggestionDiffersFromForm(data) {
    const currentGradability = this.selectedValue("image_gradability");
    const currentDrss = this.selectedValue("drss_score");

    if (!currentGradability) return Boolean(data.image_gradability);

    if (data.image_gradability && data.image_gradability !== currentGradability) {
      return true;
    }

    if (data.image_gradability === "ungradable") {
      return currentGradability !== "ungradable";
    }

    if (data.drss_score == null) return false;

    return String(data.drss_score) !== currentDrss;
  }

  selectedValue(name) {
    const checked = this.element.querySelector(`input[name="${name}"]:checked`);
    return checked ? checked.value : null;
  }

  showAppliedBanner(data) {
    if (!this.hasAiBannerTarget) return;

    this.aiBannerTarget.classList.remove("hidden");
    if (this.hasAiBannerTextTarget) {
      this.aiBannerTextTarget.textContent = this.bannerText(data, false);
    }
    if (this.hasApplyButtonTarget) this.applyButtonTarget.classList.add("hidden");
  }

  showPendingBanner(data) {
    if (!this.hasAiBannerTarget) return;

    this.aiBannerTarget.classList.remove("hidden");
    if (this.hasAiBannerTextTarget) {
      this.aiBannerTextTarget.textContent = this.bannerText(data, true);
    }
    if (this.hasApplyButtonTarget) this.applyButtonTarget.classList.remove("hidden");
  }

  bannerText(data, pending) {
    const parts = [];
    if (data.image_gradability) {
      parts.push(`Gradability: ${this.labelize(data.image_gradability)}`);
    }
    if (data.drss_score != null) {
      parts.push(`DRSS: ${data.drss_score}`);
    }

    const summary = parts.join(" · ");
    if (pending) {
      return `New AI suggestion from UWF DR Analyzer — ${summary}. Click Apply to update the form.`;
    }

    return `AI suggestion from UWF DR Analyzer — review and adjust if needed before submitting. ${summary}`;
  }

  labelize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  setRadioValue(name, value) {
    if (!value) return;

    this.element.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      const shouldCheck = input.value === value;
      if (input.checked !== shouldCheck) {
        input.checked = shouldCheck;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  clearRadioGroup(name) {
    this.element.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      if (input.checked) {
        input.checked = false;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  fieldInputs() {
    return this.element.querySelectorAll('input[name="image_gradability"], input[name="drss_score"]');
  }

  syncDrssVisibility() {
    const checked = this.element.querySelector('input[name="image_gradability"]:checked');
    const gradable = checked && checked.value === "gradable";
    const drssBlock = this.element.querySelector('[data-prescreen-crf-target="drssBlock"]');
    if (drssBlock) drssBlock.classList.toggle("hidden", !gradable);
  }
}
